import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import * as THREE from 'three';
import animateProps from 'react-animate-props';
import { debounce } from 'debounce';

import { Line2 } from 'three/examples/jsm/lines/Line2';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';

import raf from 'raf';
import Tween, { Easing } from 'tweenkle';

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

// post FX
import {
  EffectComposer,
} from 'three/examples/jsm/postprocessing/EffectComposer';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import {
  UnrealBloomPass,
} from 'three/examples/jsm/postprocessing/UnrealBloomPass';

import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader';

import { HorizontalBlurShader }
  from 'three/examples/jsm/shaders/HorizontalBlurShader';

import { VerticalBlurShader }
  from 'three/examples/jsm/shaders/VerticalBlurShader';

import {
  getAllAltitudeValues,
  getAltitudeMinYear,
  getAltitudeMaxYear,
} from '../../store/selectors';

import PointCloudLoader from './PointCloudLoader';
import PointCloudRadar from './PointCloudRadar';

import { isSafari, supportsMixBlendMode, FOV } from '../../config';

const { cos, sin, min } = Math;

const finalVertex = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
}
`;

const finalFragment = `
uniform sampler2D baseTexture;
uniform sampler2D bloomTexture;
uniform sampler2D blurTexture;
varying vec2 vUv;
vec4 getTexture( sampler2D texelToLinearTexture ) {
  return mapTexelToLinear( texture2D( texelToLinearTexture , vUv ) );
}
void main() {
  gl_FragColor = (
    getTexture( baseTexture ) +
    vec4( 1.0 ) * getTexture( bloomTexture ) -
    vec4( 0.20 ) * getTexture( blurTexture )
  );
}
`;

const pointVertex = `
attribute float alpha;
varying float vAlpha;
void main() {
  vAlpha = alpha;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
  gl_PointSize = 2.0;
}
`;

const pointFragment = `
uniform sampler2D tex1;
varying float vAlpha;
void main() {
  gl_FragColor = vec4(1.0, 1.0, 1.0, vAlpha);
}
`;

const PanoramaContainer = styled.div`
  width: 100%;
  height: 100%;
  //cursor: pointer;
  cursor: all-scroll;
  z-index: 0;
  position: relative;

  &:active {
    cursor: all-scroll;
  }

  &:before {
    content: "";
    transition: background-color 400ms;
    background-color: rgba(0,0,0,0);
    z-index: 1;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    pointer-events: none;
  }
  &.loads {
    &:before {
      background-color: rgba(0,0,0,0.6);
    }
  }

  &:after {
    content: " ";
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    pointer-events: none;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.2) 0%,
      rgba(0, 0, 0, 0) 30%,
      rgba(0, 0, 0, 0) 60%,
      rgba(0, 0, 0, 0.4) 100%
    );
  }
`;

const PanoramaElement = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
`;

const GrayscaleFade = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: white;
  box-shadow: 0 0 10vh 10vh white;
  mix-blend-mode: color;
  will-change: box-shadow, height;
  height: 0vh;
  pointer-events: none;
`;

const getCurrentAltitude = (altitude, elevation) => {
  const dy = altitude - elevation;
  return dy < 0 ? 0 : dy;
};

/**
 * Street View.
 */
class Panorama extends React.Component {
  static propTypes = {
    altitudeRunningValue: PropTypes.number.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    altitudes: PropTypes.array.isRequired,
    altitudeMaxYear: PropTypes.number.isRequired,
    altitudeMinYear: PropTypes.number.isRequired,
    coordinate: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
    }).isRequired,
    elevation: PropTypes.number.isRequired,
    depthInfo: PropTypes.shape({
      canvas: PropTypes.object,
      links: PropTypes.array,
      tiles: PropTypes.any,
    }).isRequired,
    rotation: PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      z: PropTypes.number.isRequired,
    }),
    panoramaId: PropTypes.string.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isMapUi: PropTypes.bool.isRequired,
    year: PropTypes.number.isRequired,
  };

  static defaultProps = {
    rotation: null,
  };

  /**
   * @param {object} props Properties of the instance.
   */
  constructor(props) {
    super(props);

    this.debug = false;

    this.bwLayerRef = React.createRef();
    this.panoramaRef = React.createRef();

    this.pointCloudLoader = new PointCloudLoader();

    this.radar = new PointCloudRadar();

    this.viewPitch = 0;
    this.onMouseDownMouseX = 0;
    this.onMouseDownMouseY = 0;
    this.onMouseDownLon = 0;
    this.onMouseDownLat = 0;

    this.panoramaMesh = undefined;
    this.waterLine = undefined;
    this.pointCloud = undefined;
    this.pointCloudLoaded = false;

    this.doDoubleLine = true;
    this.doubleLine = undefined;

    this.waterLineMode = 'depth';
    this.waterLineOpacity = 0;
    this.doHalo = false;
    this.haloPos = 22;

    this.panoramaAndDepthLoadCounter = 0;

    this.isUserInteracting = false;

    this.onWindowResized = debounce(this.onWindowResized.bind(this), 150);

    this.onPointCloudParsed = this.onPointCloudParsed.bind(this);
    this.onRadarDone = this.onRadarDone.bind(this);

    this.waterLineFadeTick = this.waterLineFadeTick.bind(this);
    this.waterLineFadeComplete = this.waterLineFadeComplete.bind(this);
    this.makeLine = this.makeLine.bind(this);
    this.makeWaterLineGeo = this.makeWaterLineGeo.bind(this);

    // eslint-disable-next-line max-len
    this.animateCameraDirectionAndWebGLRenderLoop = this.animateCameraDirectionAndWebGLRenderLoop.bind(this);
  }

  /**
   * React lifecycle method.
   */
  componentDidMount() {
    this.ENTIRE_SCENE = 0;
    this.BLOOM_SCENE = 1;
    this.BLUR_SCENE = 2;
    this.bloomLayer = new THREE.Layers();
    this.bloomLayer.set(this.BLOOM_SCENE);
    this.blurLayer = new THREE.Layers();
    this.blurLayer.set(this.BLUR_SCENE);

    this.darkMaterial = new THREE.MeshBasicMaterial({ color: 'black' });
    this.materials = {};

    this.camera = new THREE.PerspectiveCamera(
        FOV,
        window.innerWidth / window.innerHeight,
        0.01,
        2000,
    );
    this.camera.target = new THREE.Vector3(0, 0, 0);

    this.scene = new THREE.Scene();
    this.camera.name = 'camera';
    this.scene.add(this.camera);
    this.scene.interactables = [];

    this.cameraPosition = new THREE.Vector3(0, 0, 0);

    try {
      window.isWebGL = !!window.WebGLRenderingContext
        && !!document.createElement('canvas').getContext('experimental-webgl');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }

    this.renderer = new THREE.WebGLRenderer({
      antialias: false,
      autoClear: true,
      autoClearColor: false,
      sortObjects: false,
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.rotateSpeed = -0.025;
    this.controls.minDistance = 0.01;
    this.controls.maxDistance = 3000;

    // DEBUG true
    this.controls.screenSpacePanning = false;
    this.controls.enablePan = false;
    this.controls.enableZoom = false;
    this.controls.panSpeed = 10;
    this.controls.zoomSpeed = 2;

    if (this.debug) {
      this.controls.screenSpacePanning = true;
      this.controls.enablePan = true;
      this.controls.enableZoom = true;
    }

    // Debug disable
    if (!this.debug) {
      this.controls.maxPolarAngle = Math.PI - Math.PI * 0.25;
      this.controls.minPolarAngle = Math.PI * 0.4;
    }

    this.controls.target = new THREE.Vector3(0, 0.000005, -0.0001);
    this.controls.update();


    // ---------
    // FX
    // ---------
    const renderScene = new RenderPass(this.scene, this.camera);
    this.fxaaPass = new ShaderPass(FXAAShader);

    const pixelRatio = this.renderer.getPixelRatio();
    this.fxaaPass.material.uniforms.resolution.value
        .x = 1 / (window.innerWidth * pixelRatio);
    this.fxaaPass.material.uniforms.resolution.value
        .y = 1 / (window.innerHeight * pixelRatio);

    this.bloomPass = new UnrealBloomPass(
        new THREE.Vector2(window.innerWidth, window.innerHeight),
        1.5,
        0.4,
        0.85,
    );
    this.bloomPass.threshold = 0;
    this.bloomPass.strength = 2;
    this.bloomPass.radius = 1;

    this.bloomComposer = new EffectComposer(this.renderer);
    this.bloomComposer.renderToScreen = false;
    this.bloomComposer.addPass(renderScene);
    this.bloomComposer.addPass(this.bloomPass);


    this.vBlurPass = new ShaderPass(VerticalBlurShader);
    this.vBlurPass.uniforms.v.value = 0.0009;
    this.hBlurPass = new ShaderPass(HorizontalBlurShader);
    this.hBlurPass.uniforms.h.value = 0.0009;
    this.blurComposer = new EffectComposer(this.renderer);
    this.blurComposer.renderToScreen = false;
    this.blurComposer.addPass(renderScene);
    this.blurComposer.addPass(this.vBlurPass);
    this.blurComposer.addPass(this.hBlurPass);


    this.pointShaderMaterial = new THREE.ShaderMaterial({
      vertexShader: pointVertex,
      fragmentShader: pointFragment,
      transparent: true,
    });

    const finalPass = new ShaderPass(
        new THREE.ShaderMaterial({
          uniforms: {
            baseTexture: { value: null },
            bloomTexture: { value: this.bloomComposer.renderTarget2.texture },
            blurTexture: { value: this.blurComposer.renderTarget2.texture },
          },
          vertexShader: finalVertex,
          fragmentShader: finalFragment,
          defines: {},
        }),
        'baseTexture',
    );
    finalPass.needsSwap = true;

    this.finalComposer = new EffectComposer(this.renderer);
    this.finalComposer.addPass(renderScene);
    this.finalComposer.addPass(finalPass);
    this.finalComposer.addPass(this.fxaaPass);


    this.darkenNonBlurred = (obj) => {
      if (obj.isMesh && !this.blurLayer.test(obj.layers)) {
        this.materials[obj.uuid] = obj.material;
        // eslint-disable-next-line no-param-reassign
        obj.material = this.darkMaterial;
      }
    };
    this.darkenNonBloomed = (obj) => {
      if (obj.isMesh && !this.bloomLayer.test(obj.layers)) {
        this.materials[obj.uuid] = obj.material;
        // eslint-disable-next-line no-param-reassign
        obj.material = this.darkMaterial;
      }
    };

    this.restoreMaterial = (obj) => {
      if (this.materials[obj.uuid]) {
        // eslint-disable-next-line no-param-reassign
        obj.material = this.materials[obj.uuid];
        delete this.materials[obj.uuid];
      }
    };

    this.setGrayscaleLayer();

    // ---------
    // PANORAMA MESH
    // ---------
    const geom = new THREE.SphereGeometry(2000, 60, 40);
    geom.scale(-1, 1, 1);

    this.panoramaMesh = new THREE.Mesh(
        geom,
        new THREE.MeshBasicMaterial({
          color: 0xffffff,
          map: new THREE.TextureLoader().load(
              `${process.env.PUBLIC_URL}/placeholder.jpg`,
          ),
          opacity: 1,
        }),
    );
    this.panoramaMesh.material.transparent = false;
    this.panoramaMesh.name = 'panoramaMesh';
    this.scene.add(this.panoramaMesh);


    // ----------------
    // WATERLINE
    // ----------------

    this.lineOffset = -2.5;

    this.waterLineMode = 'depth';

    this.waterLineGeo = new LineGeometry();
    const points = [
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, 0, 0),
    ];

    // set positions from points
    const positions = [];
    for (let i = 0, l = points.length; i < l; i += 1) {
      const point = points[i];
      positions.push(point.x, point.y, point.z);
    }

    this.waterLineGeo.setPositions(positions);

    this.waterLineMat = new LineMaterial({
      color: 0xffffff,
      linewidth: 0.004, // "pixels?"
      transparent: true,
      opacity: 0,
    });

    this.waterLine = new Line2(this.waterLineGeo, this.waterLineMat);
    this.waterLine.layers.enable(this.BLOOM_SCENE);
    this.waterLine.layers.disable(this.ENTIRE_SCENE);

    this.waterLine.computeLineDistances();
    this.waterLine.scale.set(1, 1, 1);
    this.waterLine.name = 'waterLine';
    this.scene.add(this.waterLine);

    this.doubleLineMat = new LineMaterial({
      color: 0xffffff,
      linewidth: 0.000, // "pixels?"
      transparent: true,
      opacity: 0.0,
    });

    this.doubleLine = new Line2(this.waterLineGeo, this.doubleLineMat);
    this.doubleLine.layers.enable(this.BLUR_SCENE);
    this.doubleLine.layers.disable(this.ENTIRE_SCENE);
    this.doubleLine.computeLineDistances();
    this.doubleLine.scale.set(1, 1, 1);
    this.doubleLine.name = 'doubleLine';
    this.scene.add(this.doubleLine);

    // ----------------
    // ADD TO DOM
    // ----------------
    const { current } = this.panoramaRef;
    current.appendChild(this.renderer.domElement);

    if (!supportsMixBlendMode || isSafari) {
      this.bwLayerRef.current.style.display = 'none';
    }

    this.pointCloudLoader.onCompleted = this.onPointCloudParsed;
    this.radar.onCompleted = this.onRadarDone;

    window.addEventListener('resize', this.onWindowResized, false);

    // ----------------
    // START UPDATE LOOP
    // ----------------
    this.onWindowResized();
    this.animateCameraDirectionAndWebGLRenderLoop();
  }

  /**
   * React lifecycle method.
   * @param {object} nextProps
   * @param {object} nextState
   * @return {Boolean} true|false for should component update.
   */
  shouldComponentUpdate(nextProps, nextState) {
    const {
      elevation,
      altitudeRunningValue,
      isLoading,
      isMapUi,
      panoramaId,
      rotation,
    } = this.props;

    if (
      nextProps.isMapUi !== isMapUi
      || nextProps.panoramaId !== panoramaId
      || nextProps.isLoading !== isLoading
      || nextProps.rotation !== rotation
      || nextProps.elevation !== elevation
      || nextProps.altitudeRunningValue !== altitudeRunningValue
    ) {
      return true;
    }

    return false;
  }

  /**
  * React lifecycle method.
  * @param {object} prevProps
  * @param {object} prevState
  */
  componentDidUpdate(prevProps, prevState) {
    const {
      altitudeRunningValue,
      elevation,
      isMapUi,
      panoramaId,
      rotation,
    } = this.props;

    if (!prevProps.isMapUi && isMapUi) {
      // clear when returning to map
      this.clearPanorama();
    }

    if (prevProps.panoramaId !== panoramaId) {
      this.loadPanorama(panoramaId);
    }

    if (prevProps.elevation !== elevation) {
      this.checkForHalo();
    }

    if (prevProps.altitudeRunningValue !== altitudeRunningValue) {
      this.setAltitude(getCurrentAltitude(altitudeRunningValue, elevation));
    }

    if (prevProps.rotation !== rotation) {
      const { x, y, z } = {
        x: 0,
        y: 0,
        z: 0,
        ...rotation,
      };

      this.camera.position.set(x, y, z);
    }

    // restart the animation when we're done loading the streetview
    if (prevProps.isMapUi && !isMapUi) {
      this.animateCameraDirectionAndWebGLRenderLoop();
    }
  }

  /**
   * React lifecycle method.
   */
  componentWillUnmount() {
    window.removeEventListener('resize', this.onWindowResized);
  }

  /**
   * Handle window resize.
   */
  onWindowResized() {
    const { clientWidth, clientHeight } = this.panoramaRef.current;
    this.camera.aspect = clientWidth / clientHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(clientWidth, clientHeight);
    this.bloomComposer.setSize(clientWidth, clientHeight);
    this.blurComposer.setSize(clientWidth, clientHeight);
    this.finalComposer.setSize(clientWidth, clientHeight);
  }

  /**
   * OnPointCloudParsed.
   */
  onPointCloudParsed() {
    this.checkForHalo();
    if (!this.doHalo) {
      this.fadeWaterLine('out');
    }

    // Add ballCloud
    if (this.debug) {
      if (this.pointCloudLoader.ballCloudPoints) {
        this.scene.remove(this.pointCloudLoader.ballCloud);
        this.pointCloudLoader.createBallCloud();
        this.scene.add(this.pointCloudLoader.ballCloud);
      }
    }

    if (!this.doHalo && this.pointCloudLoader.ballCloudPoints) {
      this.radar.targets = this.pointCloudLoader.ballCloudPoints;
      // For debugging
      //      this.scene.add(this.radar.scanBoxPivot);
      //      this.radar.scanBox.layers.enable(this.BLOOM_SCENE);
      this.radar.start();
    } else {
      this.createCircle();
    }
  }

  /**
  * Radar done handler.
  * called by radar.update(), when it needed scan
  * and has done a full circle in rotation.
  */
  onRadarDone() {
    this.pointCloudLoader.clear();

    if (this.scanBoxPivot) {
      this.scene.remove(this.scanBoxPivot);
      this.scanBoxPivot.remove();
    }

    if (this.doHalo) {
      return;
    }

    if (this.radar.scannedLinePositions.length > 8) {
      this.makeWaterLineGeo();
    } else {
      this.createCircle();
    }

    this.updateWaterLine();
  }

  /**
  * Set latitude.
  * @param {number} curAltitude Current altitude.
  */
  setAltitude(curAltitude) {
    const {
      altitudeMaxYear,
      altitudeMinYear,
      isLoading,
      year,
      isMapUi,
    } = this.props;

    if (!this.doHalo) {
      // Update colours and FX
      // Red overlay
      const clrIntensity = 1
        - ((year - altitudeMinYear) / (altitudeMaxYear - altitudeMinYear))
        * 0.3;
      this.panoramaMesh.material.color.setRGB(1.0, clrIntensity, clrIntensity);

      // only set opacities based on altitude when conditions met:
      if (!isMapUi && this.radarDone && !isLoading && !this.waterLineFade) {
        if (curAltitude <= 0) {
          this.waterLineMat.uniforms.opacity.value = 0;
          this.doubleLineMat.uniforms.opacity.value = 0;
        } else if (curAltitude < 0.25) {
          this.waterLineMat.uniforms.opacity.value = curAltitude * 4;
          this.doubleLineMat.uniforms.opacity.value = curAltitude * 2;
        } else {
          this.waterLineMat.uniforms.opacity.value = 1;
          this.doubleLineMat.uniforms.opacity.value = 0.5;
        }
        // else, if not ticking/fading, set to zero
      } else if (!this.waterLineFade) {
        this.waterLineMat.uniforms.opacity.value = 0;
        this.doubleLineMat.uniforms.opacity.value = 0;
      }
    }

    this.updateWaterLine();
    this.setGrayscaleLayer();
  }

  /**
   * Set Gray layer.
   */
  setGrayscaleLayer() {
    // black and white dom filter
    const { altitudeRunningValue, elevation } = this.props;
    const alt = getCurrentAltitude(altitudeRunningValue, elevation);
    const bwH = Math.min(50 + (alt * 1.25) + (-30 * this.viewPitch), 70);

    const { current } = this.bwLayerRef;
    current.style.height = `${bwH}vh`;
    current.style.opacity = min(1, alt * 2);
  }

  /**
   * Waterline position handler.
   */
  updateWaterLine() {
    const { altitudeRunningValue, elevation, isLoading } = this.props;
    let wlScale = 1.0;
    let h = 0.0;

    if (!this.doHalo) {
      const alt = getCurrentAltitude(altitudeRunningValue, elevation);
      h = alt;

      if (this.waterLineMode === 'depth') {
        this.lineOffset = -2.5;
        // some offset due to car camera perspective
        h = alt + this.lineOffset;
      } else {
        this.lineOffset = -14.0;
        // h = alt + this.lineOffset;
        // circle is wider so behaves a bit differently
        const perspective = this.camera.fov * 0.1;
        h = alt * perspective + this.lineOffset;
      }

      this.waterLine.rotation.set(0, 0, 0);
      this.waterLine.scale.set(wlScale, wlScale, wlScale);
      this.waterLine.position.x = 0;
      this.waterLine.position.z = 0;
    } else { // Maximum altitude, halo-style
      const { year, altitudeMaxYear } = this.props;

      h = this.haloPos;
      wlScale = 0.04;
      const lastYear = altitudeMaxYear;
      const yearsToGo = lastYear - year;
      const orangeIntensity = yearsToGo < 250 ? 1.0 - yearsToGo / 250 : 0.0;
      const meshGreen = 1.0 - 0.2 * orangeIntensity;
      const meshBlue = 1.0 - 0.6 * orangeIntensity;
      this.panoramaMesh.material.color.setRGB(1.0, meshGreen, meshBlue);
      this.bloomPass.strength = 2;

      if (this.radarDone && !isLoading && !this.waterLineFade) {
        this.waterLineMat.uniforms.opacity.value = 1.0;
        this.doubleLineMat.uniforms.opacity.value = 0.5;
      } else if (isLoading) {
        this.waterLineMat.uniforms.opacity.value = 0.0;
        this.doubleLineMat.uniforms.opacity.value = 0.0;
      }

      // Move the halo a little to front of camera
      h += 7 * this.viewPitch;
      const haloDist = -25 + 15 * this.viewPitch;
      const vec = new THREE.Vector3(0, 0, haloDist);
      const { innerWidth, innerHeight } = window;
      vec.applyQuaternion(this.camera.quaternion);
      this.waterLine.position.copy(vec);
      this.waterLine.scale.x = (wlScale * innerWidth) / innerHeight;
      this.waterLine.scale.y = 1;
      const scaleZratio = (innerWidth / innerHeight) * 0.3;
      this.waterLine.scale.z = wlScale * scaleZratio
        + (wlScale * 0.7 * this.viewPitch);
      this.waterLine.lookAt(new THREE.Vector3(0, 0, 0));
    }

    this.waterLine.position.y = h;

    if (this.doDoubleLine) {
      this.doubleLine.position.copy(this.waterLine.position);
      this.doubleLine.scale.copy(this.waterLine.scale);
    }
  }

  /**
   * Halo handler.
   * Check for elevation and if over maximum, create circle.
   */
  checkForHalo() {
    const { altitudes, altitudeRunningValue, elevation } = this.props;
    this.doHalo = elevation >= altitudes[altitudes.length - 1];

    if (this.doHalo) {
      this.createCircle();
      this.setAltitude(getCurrentAltitude(altitudeRunningValue, elevation));
    }
  }

  /**
   * MakeWaterLineGeo.
   */
  makeWaterLineGeo() {
    this.waterLineGeo.maxInstancedCount = this.radar
        .scannedLinePositions.length / 3 - 1;
    this.waterLineGeo.setPositions(this.radar.scannedLinePositions);
    this.waterLineGeo.attributes.position.needsUpdate = true;
    this.waterLine.computeLineDistances();
    this.waterLineGeo.setDrawRange(
        0,
        this.radar.scannedLinePositions.length - 1,
    );
    this.waterLineMode = 'depth';
    this.fadeWaterLine('in');
    this.radarDone = true;
  }

  /**
   * Fallback circular waterline creator.
   */
  makeLine() {
    const circleResolution = 64;
    this.circleGeo = new THREE.CircleBufferGeometry(400, circleResolution);
    this.circleGeoPoses = this.circleGeo.attributes.position.array;
    const circlePoints = Math.floor((this.circleGeoPoses.length - 1) / 3);
    const positions = [];

    for (let i = 3; i < this.circleGeoPoses.length; i += 3) {
      positions.push(this.circleGeoPoses[i]);
      positions.push(0);
      positions.push(this.circleGeoPoses[i + 1]);
    }

    this.waterLineGeo.maxInstancedCount = circlePoints - 1;
    this.waterLineGeo.setPositions(positions);
    this.waterLine.computeLineDistances();
    this.waterLineGeo.attributes.position.needsUpdate = true;

    this.circleDone = true;

    this.waterLineMode = 'circle';
    this.updateWaterLine();
    this.fadeWaterLine('in');
  }

  /**
   * Fallback circular waterline handler.
   */
  createCircle() {
    clearTimeout(this.makeLineTimeout);
    this.makeLineTimeout = setTimeout(
        this.makeLine,
        !this.firstFadeDone ? 1500 : 500,
    );
  }

  /**
   * Waterline opacity on complete.
   */
  waterLineFadeComplete() {
    this.waterLineFade = null;
    if (!this.firstFadeDone) {
      this.firstFadeDone = true;
    }
  }

  /**
   * Waterline opacity on tick.
   * @param {object} data status of animation.
   */
  waterLineFadeTick(data) {
    this.waterLineMat.uniforms.opacity.value = data.value;
    this.doubleLineMat.uniforms.opacity.value = data.value;

    this.updateWaterLine();
  }

  /**
   * Waterline opacity handler.
   * @param {string} dir Direction.
   * @param {string} dur Duration.
   */
  fadeWaterLine(dir, dur = 400) {
    if (this.waterLineFade) {
      this.waterLineFade.stop();
    }

    const { altitudeRunningValue, elevation } = this.props;
    const curAltitude = getCurrentAltitude(altitudeRunningValue, elevation);

    if (dir === 'in') {
      const intoMin = Math.min(1, curAltitude * 4);
      this.waterLineFade = new Tween({
        start: 0, end: intoMin, duration: dur, ease: Easing.Quad.InOut,
      });
    }

    if (dir === 'out') {
      const outFrom = curAltitude < 0.25 ? curAltitude * 4 : 1;
      this.waterLineFade = new Tween({
        start: outFrom, end: 0, duration: dur, ease: Easing.Quad.InOut,
      });
    }

    this.waterLineFade.on('tick', this.waterLineFadeTick);
    this.waterLineFade.on('complete', this.waterLineFadeComplete);
    this.waterLineFade.start();
  }

  /**
   * Clears visible panorama.
   */
  clearPanorama() {
    const { depthInfo } = this.props;
    const { canvas } = depthInfo;
    this.panoramaMesh.material.map.dispose();

    if (canvas) {
      const context = canvas.getContext('2d');
      context.clearRect(0, 0, canvas.width, canvas.height);
    }

    this.clearWaterline();
  }

  /** Clears the waterline
   */
  clearWaterline() {
    if (this.waterLineFade) {
      this.waterLineFade.stop();
    }

    this.waterLineMat.uniforms.opacity.value = 0;
    this.doubleLineMat.uniforms.opacity.value = 0;
  }

  /**
   * Animation and render loop.
   */
  animateCameraDirectionAndWebGLRenderLoop() {
    const { isMapUi } = this.props;

    if (isMapUi) {
      return;
    }

    this.controls.update();

    // Values for DOM grayscale layer
    const { maxPolarAngle, minPolarAngle } = this.controls;

    this.viewPitch = (this.controls.getPolarAngle() - minPolarAngle)
      / (maxPolarAngle - minPolarAngle);

    if (!this.camera.position.equals(this.cameraPosition)) {
      this.cameraPosition.copy(this.camera.position);
    }

    this.renderScene();
    raf(this.animateCameraDirectionAndWebGLRenderLoop);
  }

  /**
   * Start loading a new panorama.
   * @param {string} panoramaId
   */
  loadPanorama(panoramaId) {
    if (!panoramaId) {
      this.clearPanorama();
      return;
    }

    this.pointCloudLoaded = false;
    this.radarDone = false;
    this.circleDone = false;

    this.clearWaterline();

    const { altitudeRunningValue, elevation, depthInfo } = this.props;
    const { canvas } = depthInfo;

    this.panoramaMesh.material.map.dispose();
    this.panoramaMesh.material.map = new THREE.Texture(canvas);
    this.panoramaMesh.material.map.needsUpdate = true;

    this.setAltitude(getCurrentAltitude(altitudeRunningValue, elevation));
    this.checkForHalo();
    this.pointCloudLoader.start(panoramaId);

    // Remove point cloud relateds here
    if (this.currentBallCloud) this.scene.remove(this.currentBallCloud);
    if (this.scanBoxPivot) this.scene.remove(this.scanBoxPivot);
  }

  /**
   * Render scene.
   */
  renderScene() {
    const {
      altitudeRunningValue,
      elevation,
      year,
      isLoading,
    } = this.props;

    // Pulsating bloom

    const h = getCurrentAltitude(altitudeRunningValue, elevation);

    this.waterLineMat.linewidth = 0.004;
    this.bloomPass.strength = 2;

    if (year <= 2300) {
      let halo = h;
      if (this.doHalo) {
        halo = 15;
        this.haloPos = 22 - cos(performance.now() / 1000);
        this.updateWaterLine();
      }
      this.bloomPass.radius = 0.90 + min(
          0.10 * halo * sin(performance.now() * 0.002),
          0.1,
      );
    } else if (!this.doHalo) {
      const lessStrength = Math.min(1.95, (year - 2300) * 0.04);
      const lessLine = Math.min(0.002, (year - 2300) * 0.0001);
      this.bloomPass.strength = Math.min(Math.max(1, 2 - lessStrength), 2);
      this.waterLineMat.linewidth = Math.min(
          0.004,
          Math.max(0.002, 0.004 - lessLine),
      );
    }

    // Opacity for shadow line
    // Lower opacity if waterline almost on ground

    if (this.doDoubleLine) {
      const quarterAlt = getCurrentAltitude(
          altitudeRunningValue,
          elevation,
      ) * 0.1;

      let dlOp = 1.0 - Math.min(0.90, Math.max(0, quarterAlt));
      if (dlOp > 0.95) {
        const dlMin = (dlOp - 0.95) * 20;
        dlOp = Math.max(0, 0.95 - dlMin);
        //  dlOp = 0.95;
      }

      if (this.radarDone && !isLoading && !this.waterLineFade) {
        this.doubleLineMat.uniforms.opacity.value = dlOp;
      }

      this.doubleLineMat.linewidth = 0.006; // + 0.001 * dlOp;
      this.doubleLine.position.y = this.lineOffset;
    }

    // render x layer biz
    this.camera.layers.set(this.BLUR_SCENE);
    this.blurComposer.render();
    this.camera.layers.set(this.BLOOM_SCENE);
    this.bloomComposer.render();
    this.camera.layers.set(this.ENTIRE_SCENE);

    this.finalComposer.render();
  }

  /**
   * React lifecycle method.
   * @return {jsx}
   */
  render() {
    const { isLoading, isMapUi } = this.props;

    return (
      <PanoramaContainer className={isLoading || isMapUi ? 'loads' : ''}>
        <PanoramaElement ref={this.panoramaRef} />
        <GrayscaleFade ref={this.bwLayerRef} />
      </PanoramaContainer>
    );
  }
}

export default connect((state) => {
  const {
    map,
    panorama,
    ui,
    altitudes,
  } = state;

  return {
    altitudeMaxYear: getAltitudeMaxYear(state),
    altitudeMinYear: getAltitudeMinYear(state),
    altitudeRunningValue: altitudes.value,
    altitudes: getAllAltitudeValues(state),
    coordinate: map.coordinate,
    elevation: map.elevation,
    isMapUi: ui.isMapUi,
    depthInfo: panorama.depthInfo,
    isLoading: panorama.progress < 100.0,
    panoramaId: panorama.id,
    year: altitudes.year,
    rotation: panorama.rotation,
  };
})(animateProps(Panorama, Panorama.defaultProps));
